import { Button, Link, TableHead, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getTime } from 'date-fns';
import { get as _get } from 'lodash';
import * as React from 'react';
import { deleteBtn, resumeBtn, viewDetailsBtn } from '../../../Constants/Constants';
import { GenericProductType } from '../../../types/type';
import { getClaimDescription } from '../../../utils/Policy-Util';
import { getClaimStageText, getClaimType, getPrettyClaimNumber } from '../../../utils/Utilities';
import ClaimDescriptionLink from '../../shared/ClaimDescriptionLink/ClaimDescriptionLink';
import ProgressTrackerMini from '../../shared/ProgressTracker/ProgressTrackerMini';
import { TablePaginationActions } from '../../shared/TablePagination/TablePagination';

function createData(claim: any, isDraft: boolean) {
  return {
    name: claim.claimNumber,
    description: getClaimDescription(claim),
    date: claim.lossDate,
    progressStep: getClaimStageText(claim.claimStatusCode),
    vehicleDetail: claim.vehicleDetail,
    isFullClaim: !isDraft,
    policyType: _get(claim, 'policyType', ''),
  };
}

interface rowData {
  name: string;
  description: string;
  date: string;
  progressStep: string;
  vehicleDetail: any[];
  policyType: string;
}

export default function ClaimList(props: any) {
  const theme = useTheme();
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  // Create rows and sort by date
  let rows = props.claims.map((claim: any) =>
    createData(claim, props.isDraft || claim.claimNumber.substring(0, 2) === 'TC')
  );
  rows = rows.sort((obj1: rowData, obj2: rowData) => {
    return getTime(new Date(obj2.date)) - getTime(new Date(obj1.date));
  });

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
      <Table style={{ tableLayout: 'fixed' }} sx={{ minWidth: 350 }} aria-label="custom pagination table">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={theme.custom.styles.adjusterNameStyle}>
              {!!props.isDraft || !!props.combinedList ? 'Claim Details' : 'Claim'}
            </TableCell>
            <TableCell align="center" style={theme.custom.styles.adjusterNameStyle}>
              {!!props.isDraft || !!props.combinedList ? 'Incident Date' : 'Incident Date'}
            </TableCell>
            <TableCell align="center" style={theme.custom.styles.adjusterNameStyle}>
              Status
            </TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(
            (row: any) => (
              <TableRow key={row.name}>
                <TableCell align="left">
                  <Link component="button" variant="body1" underline="hover" onClick={() => props.handleNext(row)}>
                    {!!props.isDraft || (!row.isFullClaim && !!props.combinedList)
                      ? 'Saved Draft'
                      : 'Claim ' + getPrettyClaimNumber(row.name)}
                  </Link>

                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    <ClaimDescriptionLink
                      description={row.description}
                      maskInDatadog={getClaimType(row.policyType) === GenericProductType.WorkersComp}
                      onMultipleVehiclesClick={
                        props.onMultipleVehiclesClick ? () => props.onMultipleVehiclesClick(row.name) : undefined
                      }
                    />
                  </Typography>
                </TableCell>
                <TableCell style={{ ...theme.custom.styles.disclaimerFont, width: 160 }} align="center">
                  <Typography variant="body1">{row.date}</Typography>
                </TableCell>
                <TableCell style={{ width: 160 }} align="center">
                  <Typography style={{ width: '33%', marginLeft: '33%', textAlign: 'left' }} variant="body1">
                    {row.progressStep}
                  </Typography>

                  <ProgressTrackerMini marginLeft={'33%'} step={row.progressStep} name={row.name} />
                </TableCell>
                <TableCell style={{ width: 160, textAlign: 'left' }} align="right">
                  {!!props.isDraft && !props.hideDelete && (
                    <Button
                      onClick={() => props.handleDelete(row)}
                      sx={{
                        ...theme.custom.styles.genericDestructiveTertiaryButton,
                        marginLeft: '1rem',
                      }}
                    >
                      {deleteBtn}
                    </Button>
                  )}

                  {!!props.draftsAndFull && !row.isFullClaim && (
                    <Button
                      onClick={() => props.handleNext(row)}
                      sx={{
                        ...theme.custom.styles.genericTertiaryButton,
                      }}
                    >
                      {resumeBtn}
                    </Button>
                  )}

                  {(!props.draftsAndFull || !!row.isFullClaim) && (
                    <Button
                      onClick={() => props.handleNext(row)}
                      sx={{
                        ...theme.custom.styles.genericTertiaryButton,
                        '&.MuiButtonBase-root:hover': {
                          //TODO: Could be different for opcos
                          // bgcolor: theme.custom.colors.primaryButtonHoverColor,
                          // color: theme.custom.styles.ctaNextButton.color,
                        },
                      }}
                    >
                      {!!props.isDraft || (!row.isFullClaim && !!props.combinedList) ? resumeBtn : viewDetailsBtn}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={4}
              count={rows.length}
              rowsPerPage={5}
              rowsPerPageOptions={[]}
              page={page}
              onPageChange={handleChangePage}
              ActionsComponent={TablePaginationActions}
              sx={{
                borderBottom: '0px',
                '.MuiTablePagination-displayedRows': {
                  fontFamily: theme.custom.styles.body2,
                },
              }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
